import { Box, ThemeProvider, useTheme } from '@mui/material';
import { ExpandMoreOutlined } from '@mui/icons-material';
import { PaloThemeProvider, tokens } from '@synergy/theme-palo';
import { ReactElement, ReactNode, useState } from 'react';

const c = tokens.color;

export interface PaloRailSection {
    key: string;
    label: ReactNode;
    /** Right-aligned affordance, typically an add button. */
    action?: ReactNode;
    /** Leave out for a heading that only carries its action. */
    content?: ReactNode;
    /**
     * Dims the heading. For a slot that is agreed but not filled yet, so the
     * order of the rail is already the one it will keep.
     */
    muted?: boolean;
}

export interface PaloSideRailProps {
    sections: PaloRailSection[];
    /** Quiet block at the foot, e.g. who changed the document and when. */
    footer?: ReactNode;
    width?: number;
    defaultOpen?: boolean;
    /** Word shown sideways while the rail is folded away. */
    collapsedLabel?: ReactNode;
}

/** Folded width: enough for the chevron and a word turned on its side. */
const RAIL_WIDTH = 32;

const RAIL_OPEN_KEY = 'palo.rail.open';

/**
 * Whether the rail was left open.
 *
 * Folding it away is worth 269 pixels to the form, and whoever works that way
 * works that way in every document — so the choice outlives the page (#2208).
 * Blocked storage simply means the rail opens as it always did.
 */
const readRailOpen = (fallback: boolean): boolean => {
    try {
        const stored = localStorage.getItem(RAIL_OPEN_KEY);

        return null === stored ? fallback : 'open' === stored;
    } catch {
        return fallback;
    }
};

const writeRailOpen = (open: boolean): void => {
    try {
        localStorage.setItem(RAIL_OPEN_KEY, open ? 'open' : 'closed');
    } catch {
        // No storage: the rail forgets between visits.
    }
};

/** Quiet count next to a section heading; renders nothing for none. */
export const PaloRailCount = ({ value }: { value?: number }): ReactElement | null =>
    value ? (
        <Box
            component="span"
            sx={{
                fontSize: '11px',
                fontWeight: 500,
                letterSpacing: 'normal',
                color: c.ink3,
                fontVariantNumeric: 'tabular-nums',
            }}
        >
            {value}
        </Box>
    ) : null;

/**
 * The right-hand rail of the Belegmaske: stacked sections, no tabs.
 *
 * The panel it replaces divided the same column into tabs, and nobody could say
 * which of them mattered right now — "welcher ist jetzt der wichtigste?"
 * (Stefan Graf, Besprechung 2026-09-03). Notices moved to the head of the mask
 * and the history to the foot, so what is left here is the attachments, and a
 * list needs no tabs. The remaining sections are laid out for what comes later
 * — tags, sharing, assigning — so their arrival changes nothing about the shape.
 *
 * Chrome renders under the PALO theme, section content under the ambient theme,
 * so legacy panels keep working inside it.
 */
export const PaloSideRail = ({
    sections,
    footer,
    width = 300,
    defaultOpen = true,
    collapsedLabel,
}: PaloSideRailProps): ReactElement => {
    const ambientTheme = useTheme();
    const [open, setOpen] = useState(() => readRailOpen(defaultOpen));

    const toggleOpen = (): void =>
        setOpen(value => {
            writeRailOpen(!value);

            return !value;
        });

    const toggle = (
        <Box
            role="button"
            aria-label={open ? 'Einklappen' : 'Ausklappen'}
            onClick={toggleOpen}
            sx={{
                display: 'flex',
                alignItems: 'center',
                color: c.ink3,
                cursor: 'pointer',
                flexShrink: 0,
                '&:hover': { color: c.ink },
                '& svg': {
                    fontSize: 17,
                    transition: 'transform .15s',
                    // Points at the edge the rail folds against, and away from
                    // it once folded.
                    transform: open ? 'rotate(-90deg)' : 'rotate(90deg)',
                },
            }}
        >
            <ExpandMoreOutlined />
        </Box>
    );

    if (!open) {
        return (
            <Box
                sx={{
                    flexShrink: 0,
                    width: RAIL_WIDTH,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '7px 0',
                    borderLeft: `1px solid ${c.line}`,
                    backgroundColor: c.surfaceAlt,
                    userSelect: 'none',
                }}
            >
                <PaloThemeProvider>
                    {toggle}
                    <Box
                        role="button"
                        onClick={toggleOpen}
                        sx={{
                            writingMode: 'vertical-rl',
                            fontSize: '11.5px',
                            color: c.ink2,
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                            '&:hover': { color: c.ink },
                        }}
                    >
                        {collapsedLabel ?? sections[0]?.label}
                    </Box>
                </PaloThemeProvider>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                flexShrink: 0,
                width,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                borderLeft: `1px solid ${c.line}`,
                backgroundColor: c.surface,
            }}
        >
            <PaloThemeProvider>
                <Box
                    sx={{
                        minHeight: 34,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        padding: '0 8px',
                        borderBottom: `1px solid ${c.line}`,
                        backgroundColor: c.surfaceAlt,
                        flexShrink: 0,
                    }}
                >
                    {toggle}
                </Box>
            </PaloThemeProvider>

            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
                {sections.map(section => (
                    <Box key={section.key} sx={{ borderBottom: `1px solid ${c.line}` }}>
                        <PaloThemeProvider>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '9px 12px',
                                    fontSize: '11px',
                                    fontWeight: 620,
                                    letterSpacing: '.07em',
                                    textTransform: 'uppercase',
                                    color: section.muted ? c.ink3 : c.ink2,
                                }}
                            >
                                <Box sx={{ minWidth: 0, flex: 1 }}>{section.label}</Box>
                                {section.action}
                            </Box>
                        </PaloThemeProvider>
                        {section.content && (
                            <ThemeProvider theme={ambientTheme}>
                                {/* Panels here were written for the width of the
                                    window; sideways scrolling would hide the
                                    start of every line. */}
                                <Box
                                    sx={{
                                        padding: '0 12px 12px',
                                        overflowX: 'hidden',
                                        wordBreak: 'break-word',
                                        '& *': { minWidth: 0 },
                                    }}
                                >
                                    {section.content}
                                </Box>
                            </ThemeProvider>
                        )}
                    </Box>
                ))}

                {footer && (
                    <PaloThemeProvider>
                        <Box sx={{ padding: '12px', fontSize: '11.5px', color: c.ink3, lineHeight: 1.6 }}>{footer}</Box>
                    </PaloThemeProvider>
                )}
            </Box>
        </Box>
    );
};
